/**
 * Layout — Salesforce-inspired light enterprise shell
 * Sidebar: white, SF blue active states, clean typography
 * Header: white, light border, breadcrumb
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { useLabel } from "@/contexts/LabelContext";
import { NAV } from "@/constants/testIds";
import {
  LayoutDashboard, Settings2, Calendar, Users, ShieldCheck,
  LogOut, ChevronLeft, ChevronRight, Building2, Zap,
  Target, Inbox, Mail, Sparkles, ClipboardList,
  MessageSquarePlus, Menu, X, TrendingUp, Activity,
  FileText, UserCog, MessageCircle, ShieldAlert, RefreshCw,
  HelpCircle, BarChart2, Bell, BellRing, Check, CheckCheck, ChevronRight as ChevRight,
  Package, Handshake, DollarSign, Send, CalendarRange, QrCode, BookOpen, Compass, Banknote,
  Hotel, Video, CalendarPlus,
} from "lucide-react";
import { CoPilotDrawer } from "@/components/CoPilotDrawer";
import CRMGlobalSearch from "@/components/CRMGlobalSearch";
import { notificationsApi } from "@/services/api";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Inbox badge counter — Phase B: unread-count; Phase A fallback: actionable ─
function useInboxBadge() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const poll = () => {
      const token   = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Phase B: try per-user unread count first
      fetch(`${API}/api/communication/inbox/unread-count`, {
        headers, credentials: "include",
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!d) return fetchActionable(headers);
          if (d.read_tracking_enabled === false) return fetchActionable(headers);
          setCount(d.unread || 0);
        })
        .catch(() => fetchActionable(headers));
    };
    const fetchActionable = (headers) => {
      fetch(`${API}/api/communication/inbox/summary`, { headers, credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) setCount(d.total_actionable || 0); })
        .catch(() => {});
    };
    poll();
    const id = setInterval(poll, 60_000);
    return () => clearInterval(id);
  }, []);
  return count;
}

// ── DCC-03: Notification bell hook (30s polling, paused when tab hidden) ─────
function useNotificationBell() {
  const [unread, setUnread]           = useState(0);
  const [notifications, setNotifs]    = useState([]);
  const [loading, setLoading]         = useState(false);
  const intervalRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnread(res.data?.count ?? 0);
    } catch { /* silent — 401/403 on logged-out state is expected */ }
  }, []);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list({ limit: 20, skip: 0 });
      setNotifs(res.data || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const startPolling = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        if (document.visibilityState !== "hidden") fetchCount();
      }, 30_000);
    };
    startPolling();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") fetchCount();
    });
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchCount]);

  const markRead = useCallback(async (nid) => {
    try {
      await notificationsApi.markRead(nid);
      setNotifs(prev => prev.map(n => n.id === nid ? { ...n, status: "READ" } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifs(prev => prev.map(n => ({ ...n, status: "READ" })));
      setUnread(0);
    } catch { /* silent */ }
  }, []);

  const dismiss = useCallback(async (nid) => {
    try {
      await notificationsApi.dismiss(nid);
      setNotifs(prev => {
        const removed = prev.find(n => n.id === nid);
        if (removed?.status === "UNREAD") setUnread(c => Math.max(0, c - 1));
        return prev.filter(n => n.id !== nid);
      });
    } catch { /* silent */ }
  }, []);

  return { unread, notifications, loading, fetchFeed, markRead, markAllRead, dismiss };
}

// ── Notification type labels & icons ────────────────────────────────────────
const NOTIF_TYPE_META = {
  DOC_SUBMITTED_FOR_REVIEW: { label: "Document submitted",     color: "#2563EB" },
  DOC_APPROVED:             { label: "Document approved",      color: "#16A34A" },
  DOC_REJECTED:             { label: "Document rejected",      color: "#DC2626" },
  COMM_FAILED:              { label: "Message delivery failed", color: "#D97706" },
  THREAD_MENTION:           { label: "Mentioned in thread",    color: "#7C3AED" },
  EVENT_APPROVAL_REQUESTED: { label: "Approval required",      color: "#EA580C" },
  EVENT_CONFIRMED:          { label: "Event confirmed",        color: "#059669" },
  CLOSEOUT_EXCEPTION:       { label: "Closeout exception",     color: "#B45309" },
};

// ── Notification Bell Component ───────────────────────────────────────────────
function NotificationBell() {
  const [open, setOpen]     = useState(false);
  const panelRef            = useRef(null);
  const navigate            = useNavigate();
  const {
    unread, notifications, loading,
    fetchFeed, markRead, markAllRead, dismiss,
  } = useNotificationBell();

  // Close panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    setOpen(o => {
      if (!o) fetchFeed();
      return !o;
    });
  };

  const handleNotifClick = (n) => {
    if (n.status === "UNREAD") markRead(n.id);
    if (n.deep_link) {
      navigate(n.deep_link);
      setOpen(false);
    }
  };

  const unreadItems  = notifications.filter(n => n.status === "UNREAD");
  const hasUnread    = unread > 0;

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell-wrapper">
      {/* Bell trigger button */}
      <button
        data-testid="notification-bell-btn"
        onClick={handleOpen}
        className="relative flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
        style={{
          color: hasUnread ? "var(--brand-gold, #B8860B)" : "#78716C",
          background: open ? "#F0EAE0" : "transparent",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F0EAE0"; }}
        onMouseLeave={e => { e.currentTarget.style.background = open ? "#F0EAE0" : "transparent"; }}
        aria-label={`Notifications${hasUnread ? ` (${unread} unread)` : ""}`}
      >
        {hasUnread
          ? <BellRing className="w-4 h-4" />
          : <Bell    className="w-4 h-4" />
        }
        {/* Unread badge */}
        {hasUnread && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full text-white font-bold"
            style={{
              background: "#DC2626",
              fontSize: "9px",
              minWidth: "16px",
              height: "16px",
              padding: "0 3px",
              lineHeight: 1,
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          data-testid="notification-panel"
          className="absolute right-0 mt-2 z-50 flex flex-col rounded-xl shadow-xl overflow-hidden"
          style={{
            width: "360px",
            maxHeight: "480px",
            background: "#FFFFFF",
            border: "1px solid #E7E0D8",
            top: "100%",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 flex-shrink-0"
            style={{ borderBottom: "1px solid #F0EAE0" }}
          >
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" style={{ color: "#1C1917" }} />
              <span className="text-sm font-semibold" style={{ color: "#1C1917" }}>
                Notifications
              </span>
              {hasUnread && (
                <span
                  className="text-xs font-medium px-1.5 py-0.5 rounded-full"
                  style={{ background: "#FEE2E2", color: "#DC2626" }}
                >
                  {unread} unread
                </span>
              )}
            </div>
            {hasUnread && (
              <button
                data-testid="notification-mark-all-read-btn"
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs font-medium transition-colors"
                style={{ color: "#2563EB" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#1D4ED8"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#2563EB"; }}
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto flex-1" style={{ maxHeight: "380px" }}>
            {loading && (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin" style={{ color: "#78716C" }} />
              </div>
            )}
            {!loading && notifications.length === 0 && (
              <div
                data-testid="notification-empty-state"
                className="flex flex-col items-center justify-center py-10 gap-2"
              >
                <Bell className="w-8 h-8" style={{ color: "#D6CEBF" }} />
                <p className="text-sm" style={{ color: "#78716C" }}>No notifications</p>
              </div>
            )}
            {!loading && notifications.map((n) => {
              const meta  = NOTIF_TYPE_META[n.notification_type] || { label: n.notification_type, color: "#57534E" };
              const isNew = n.status === "UNREAD";
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  className="group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors"
                  style={{
                    background: isNew ? "#FAFAF9" : "#FFFFFF",
                    borderBottom: "1px solid #F5F0EB",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#F5F0EB"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isNew ? "#FAFAF9" : "#FFFFFF"; }}
                  onClick={() => handleNotifClick(n)}
                >
                  {/* Status dot */}
                  <div className="flex-shrink-0 mt-1.5">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ background: isNew ? meta.color : "#D6CEBF" }}
                    />
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className="text-sm leading-snug"
                        style={{ color: "#1C1917", fontWeight: isNew ? 600 : 400 }}
                      >
                        {n.title}
                      </p>
                      {/* Dismiss button */}
                      <button
                        data-testid={`notification-dismiss-${n.id}`}
                        onClick={(e) => { e.stopPropagation(); dismiss(n.id); }}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: "#78716C" }}
                        aria-label="Dismiss notification"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-xs mt-0.5 leading-snug" style={{ color: "#78716C" }}>
                      {n.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                        style={{ background: `${meta.color}18`, color: meta.color }}
                      >
                        {meta.label}
                      </span>
                      <span className="text-[10px]" style={{ color: "#A8A29E" }}>
                        {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  </div>
                  {/* Mark read button for unread items */}
                  {isNew && (
                    <button
                      data-testid={`notification-read-${n.id}`}
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: "#2563EB" }}
                      aria-label="Mark as read"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer — always show "View all notifications" link */}
          <div
            className="flex items-center justify-center px-4 py-2 flex-shrink-0"
            style={{ borderTop: "1px solid #F0EAE0" }}
          >
            <button
              data-testid="notification-view-all-btn"
              onClick={() => { navigate("/notifications"); setOpen(false); }}
              className="text-xs font-medium flex items-center gap-1 transition-colors"
              style={{ color: "#2563EB" }}
            >
              View all notifications <ChevRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Route-to-context map (most specific first) ────────────────────
const ROUTE_CONTEXT = [
  { pattern: /^\/ar\/credit-notes\/[^/]+/, section: "FINANCE",    page: "Credit Note Detail" },
  { pattern: /^\/ar\/credit-notes/,       section: "FINANCE",    page: "Credit Notes" },
  { pattern: /^\/ar\/refunds/,            section: "FINANCE",    page: "Refund Requests" },
  { pattern: /^\/ar\/receipts\/[^/]+/, section: "FINANCE",         page: "Receipt Detail" },
  { pattern: /^\/ar\/receipts/,       section: "FINANCE",         page: "Customer Receipts" },
  { pattern: /^\/ar\/master-data/,    section: "FINANCE",         page: "AR Configuration" },
  { pattern: /^\/ar\/gateway-payments/, section: "FINANCE",       page: "Gateway Payments" },
  { pattern: /^\/ar\/invoices\/[^/]+/, section: "FINANCE",        page: "Invoice Detail" },
  { pattern: /^\/ar\/invoices/,       section: "FINANCE",         page: "AR Invoices" },
  { pattern: /^\/ar\/aging/,          section: "FINANCE",         page: "AR Aging Dashboard" },
  { pattern: /^\/ar\/customers\/[^/]+\/statement/, section: "FINANCE", page: "Customer AR 360°" },
  { pattern: /^\/gl\/journals\/[^/]+/, section: "FINANCE",              page: "GL Journal Detail" },
  { pattern: /^\/gl\/journals/,        section: "FINANCE",              page: "GL Journals" },
  { pattern: /^\/engage\/leads\/[^/]+/,    section: "CRM",            page: "Lead Detail" },
  { pattern: /^\/engage\/leads/,           section: "CRM",            page: "Leads" },
  { pattern: /^\/engage\/inbox/,           section: "CRM",            page: "Inbox" },
  { pattern: /^\/crm\/inbox/,              section: "CRM",            page: "Communication Inbox" },
  { pattern: /^\/crm\/accounts\/[^/]+/,   section: "CRM",            page: "Account Detail" },
  { pattern: /^\/crm\/accounts/,          section: "CRM",            page: "Accounts" },
  { pattern: /^\/crm\/contacts\/[^/]+/,   section: "CRM",            page: "Contact Detail" },
  { pattern: /^\/crm\/contacts/,          section: "CRM",            page: "Contacts" },
  { pattern: /^\/crm\/opportunities/,     section: "CRM",            page: "Opportunities" },
  { pattern: /^\/venue-commerce\/partner-applications\/new/, section: "VENUE_COMMERCE", page: "New Partner Application" },
  { pattern: /^\/venue-commerce\/partner-applications\/[^/]+/, section: "VENUE_COMMERCE", page: "Partner Application Detail" },
  { pattern: /^\/venue-commerce\/partner-applications/, section: "VENUE_COMMERCE", page: "Partner Applications" },
  { pattern: /^\/venue-preview\/[^/]+/,                 section: "VENUE_COMMERCE", page: "Venue Preview (Internal)" },
  { pattern: /^\/destination-preview\/[^/]+/,           section: "VENUE_COMMERCE", page: "Destination Preview (Internal)" },
  { pattern: /^\/connect\/meetings\/[^/]+/,              section: "ARGOV_CONNECT",  page: "Meeting Detail" },
  { pattern: /^\/connect\/schedule/,                     section: "ARGOV_CONNECT",  page: "Schedule Meeting" },
  { pattern: /^\/connect\/meetings/,                     section: "ARGOV_CONNECT",  page: "Meetings" },
  { pattern: /^\/venue-commerce\/catalogue/, section: "VENUE_COMMERCE", page: "Venue Catalogue" },
  { pattern: /^\/venue-commerce\/[^/]+/, section: "VENUE_COMMERCE", page: "Venue Profile" },
  { pattern: /^\/crm\/bookings\/[^/]+/,  section: "CRM",            page: "Booking Detail" },
  { pattern: /^\/crm\/bookings/,         section: "CRM",            page: "Bookings" },
  { pattern: /^\/crm\/partners\/[^/]+/,  section: "CRM",            page: "Partner Profile" },
  { pattern: /^\/crm\/partners/,         section: "CRM",            page: "Partner Directory" },
  { pattern: /^\/crm\/catalog/,          section: "CRM",            page: "Service Catalog" },
  { pattern: /^\/crm\/activities/,       section: "CRM",            page: "Activities" },
  { pattern: /^\/crm/,                    section: "CRM",            page: "Overview" },
  { pattern: /^\/imagine\/events\/[^/]+/, section: "PLANNING",       page: "Event Planning" },
  { pattern: /^\/imagine/,               section: "PLANNING",       page: "Events" },
  { pattern: /^\/events\/[^/]+\/changes\/[^/]+/, section: "EXECUTION", page: "Change Detail" },
  { pattern: /^\/events\/[^/]+\/threads/, section: "EXECUTION",      page: "Threads" },
  { pattern: /^\/events\/[^/]+\/360/,    section: "EXECUTION",      page: "Event 360" },
  { pattern: /^\/events/,               section: "EXECUTION",      page: "Active Events" },
  { pattern: /^\/uat/,                  section: "UAT",            page: "Control Center" },
  { pattern: /^\/business-setup/,            section: "SETTINGS",        page: "Business Setup" },
  { pattern: /^\/setup-studio/,              section: "SETTINGS",        page: "Setup Studio" },
  { pattern: /^\/email-channel/,             section: "SETTINGS",        page: "Email Channel" },
  { pattern: /^\/administration\/users/,     section: "ADMINISTRATION", page: "Users & Roles" },
  { pattern: /^\/administration\/audit/,     section: "ADMINISTRATION", page: "Audit" },
  { pattern: /^\/administration\/integrations/, section: "ADMINISTRATION", page: "Integrations" },
  { pattern: /^\/administration/,            section: "ADMINISTRATION", page: "Administration" },
  { pattern: /^\/enterprise/,               section: "ADMINISTRATION", page: "Enterprise Structure" },
  { pattern: /^\/journeys\//,          section: "CRM",            page: "Customer Journey" },
  { pattern: /^\/planner\/cases/,      section: "PLANNING",       page: "My Planning Cases" },
  { pattern: /^\/guide/,              section: "HELP",           page: "User Guide" },
  { pattern: /^\/home/,                section: "HOME",           page: "Home" },
  { pattern: /^\/dashboard/,           section: "HOME",           page: "Platform Dashboard" },
  { pattern: /^\/marketing\/public-portal\/analytics/, section: "MARKETING", page: "Demand Analytics" },
];

function resolveContext(pathname) {
  for (const { pattern, section, page } of ROUTE_CONTEXT) {
    if (pattern.test(pathname)) return { section, page };
  }
  return { section: "PLATFORM", page: "" };
}

// ── Navigation sections — full catalogue (fallback for unmapped roles) ────────
const NAV_SECTIONS = [
  {
    id: "home",
    label: null,
    items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ],
  },
  {
    id: "crm_sales",
    label: "CRM & SALES",
    items: [
      { to: "/engage/leads",      icon: Target,        label: "Leads",           testId: NAV.leadsLink },
      { to: "/crm/accounts",      icon: Building2,     label: "Accounts",        testId: "nav-accounts" },
      { to: "/crm/contacts",      icon: Users,         label: "Contacts",        testId: "nav-contacts" },
      { to: "/crm/opportunities", icon: TrendingUp,    label: "Opportunities",   testId: "nav-opportunities" },
      { to: "/crm/bookings",      icon: FileText,      label: "Bookings",        testId: "nav-bookings" },
      { to: "/crm/partners",      icon: Handshake,     label: "Partners",        testId: "nav-partners" },
      { to: "/crm/catalog",       icon: Package,       label: "Service Catalog", testId: "nav-catalog" },
      { to: "/crm/activities",    icon: Activity,      label: "Activities",      testId: "nav-activities" },
      { to: "/engage/inbox",      icon: Inbox,         label: "Inquiries",       testId: "nav-inbox" },
      { to: "/crm/inbox",         icon: MessageCircle, label: "Communications",  testId: "nav-comm-inbox" },
    ],
  },
  {
    id: "event_management",
    label: "EVENT MANAGEMENT",
    items: [
      { to: "/imagine", icon: Sparkles,  label: "Event Planning & Design", testId: NAV.eventsLink },
      { to: "/events",  icon: Calendar,  label: "All Events",              testId: "nav-active-events" },
    ],
  },
  {
    id: "argov_connect",
    label: "ARGOV CONNECT",
    items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",        testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ],
  },
  {
    id: "uat",
    label: "UAT",
    feedbackAction: true,
    items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ],
  },
  {
    id: "help",
    label: "HELP",
    items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ],
  },
  {
    id: "administration",
    label: "ADMINISTRATION",
    items: [
      { to: "/dashboard",            icon: LayoutDashboard, label: "Platform Overview",    testId: NAV.dashboardLink },
      { to: "/enterprise",           icon: Building2,       label: "Enterprise",           testId: "nav-enterprise" },
      { to: "/administration",       icon: ShieldCheck,     label: "Administration",       testId: NAV.adminLink },
    ],
  },
];

// ── Role-display names ─────────────────────────────────────────────────────────
// CRM-NAV-AUDIT-01: added RELATIONSHIP_MANAGER, PROJECT_MANAGER, FINANCE_APPROVER
const ROLE_LABELS = {
  BUSINESS_OWNER:         "Business Owner",
  CRM_MANAGER:            "Sales Manager",
  RELATIONSHIP_MANAGER:   "Client Relationship Manager",
  SALES_CRM_USER:         "Client Relationship Manager",
  EVENT_POC:              "Event POC",
  TENANT_ADMIN:           "System Administrator",
  PLATFORM_ADMIN:         "Platform Admin",
  EVENT_MANAGER:          "Event Manager",
  EVENT_PLANNER_DESIGNER: "Event Planner",
  PROJECT_MANAGER:        "Event Manager",
  FINANCE_APPROVER:       "Finance Manager",
  OPERATIONS_COORDINATOR: "Operations Coordinator",
};

// ── Role-specific nav IDs (legacy reference — authoritative config is ROLE_NAV_SECTIONS_MAP) ─
const _ROLE_NAV_IDS = {
  BUSINESS_OWNER:  ["home", "crm_no_comm_inbox", "uat", "administration_reports"],
  CRM_MANAGER:     ["home", "crm", "uat", "administration_reports"],
  SALES_CRM_USER:  ["home", "crm_no_reports", "uat"],
  EVENT_POC:       ["home", "poc", "uat"],
  TENANT_ADMIN:    ["administration_full"],
};

// ── CRM base items shared across roles that have full CRM pipeline access ─────
// CRM-NAV-AUDIT-01: "Inbox" renamed to "Inquiries" (raw web/email form intake)
const _CRM_ITEMS_BASE = () => [
  { to: "/engage/leads",      icon: Target,        label: "Leads",         testId: NAV.leadsLink },
  { to: "/crm/accounts",      icon: Building2,     label: "Accounts",      testId: "nav-accounts" },
  { to: "/crm/contacts",      icon: Users,         label: "Contacts",      testId: "nav-contacts" },
  { to: "/crm/opportunities", icon: TrendingUp,    label: "Opportunities", testId: "nav-opportunities" },
  { to: "/crm/bookings",      icon: FileText,      label: "Bookings",      testId: "nav-bookings" },
  { to: "/crm/partners",      icon: Handshake,     label: "Partners",      testId: "nav-partners" },
  { to: "/crm/activities",    icon: Activity,      label: "Activities",    testId: "nav-activities" },
  { to: "/engage/inbox",      icon: Inbox,         label: "Inquiries",     testId: "nav-inbox" },
];

const ROLE_NAV_SECTIONS_MAP = {
  // ── Business Owner: CRM pipeline + Intelligence ───────────────────────────
  BUSINESS_OWNER: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "crm_sales", label: "CRM & SALES", items: _CRM_ITEMS_BASE() },
    { id: "venue_commerce", label: "VENUE COMMERCE", items: [
      { to: "/venue-commerce/catalogue",             icon: Hotel,     label: "Venue Catalogue",      testId: "nav-venue-catalogue" },
      { to: "/venue-commerce/partner-applications",  icon: FileText,  label: "Partner Applications", testId: "nav-venue-partner-apps" },
      { to: "/destination-preview/hyderabad",        icon: Compass,   label: "Destination Preview",  testId: "nav-destination-preview" },
    ]},
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",         testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ]},
    { id: "intelligence", label: "INTELLIGENCE & REPORTS", items: [
      { to: "/marketing/public-portal/analytics", icon: BarChart2, label: "Demand Analytics", testId: "nav-demand-analytics" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Sales Manager: Full CRM & Sales pipeline + Analytics ─────────────────
  CRM_MANAGER: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "crm_sales", label: "CRM & SALES", items: [
      ..._CRM_ITEMS_BASE(),
      { to: "/crm/catalog", icon: Package,       label: "Service Catalog",  testId: "nav-catalog" },
      { to: "/crm/inbox",   icon: MessageCircle, label: "Communications",   testId: "nav-comm-inbox" },
    ]},
    { id: "venue_commerce", label: "VENUE COMMERCE", items: [
      { to: "/venue-commerce/catalogue",            icon: Hotel,     label: "Venue Catalogue",      testId: "nav-venue-catalogue" },
      { to: "/venue-commerce/partner-applications", icon: FileText,  label: "Partner Applications", testId: "nav-venue-partner-apps" },
      { to: "/destination-preview/hyderabad",       icon: Compass,   label: "Destination Preview",  testId: "nav-destination-preview" },
    ]},
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",         testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ]},
    { id: "intelligence", label: "INTELLIGENCE & REPORTS", items: [
      { to: "/marketing/public-portal/analytics", icon: BarChart2, label: "Demand Analytics", testId: "nav-demand-analytics" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Client Relationship Manager: My clients pipeline + Comms ─────────────
  RELATIONSHIP_MANAGER: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "crm_sales", label: "CRM & SALES", items: [
      ..._CRM_ITEMS_BASE(),
      { to: "/crm/inbox", icon: MessageCircle, label: "Communications", testId: "nav-comm-inbox" },
    ]},
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",         testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Event Manager: CRM + Event lifecycle + Sourcing + Finance ────────────
  PROJECT_MANAGER: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "crm_sales", label: "CRM & SALES", items: [
      { to: "/crm/accounts",      icon: Building2,  label: "Accounts",          testId: "nav-accounts" },
      { to: "/crm/contacts",      icon: Users,      label: "Contacts",          testId: "nav-contacts" },
      { to: "/crm/opportunities", icon: TrendingUp, label: "Opportunities",     testId: "nav-opportunities" },
      { to: "/crm/bookings",      icon: FileText,   label: "Bookings",          testId: "nav-bookings" },
      { to: "/crm/catalog",       icon: Package,    label: "Service Catalog",   testId: "nav-catalog" },
      { to: "/crm/activities",    icon: Activity,   label: "Activities",        testId: "nav-activities" },
    ]},
    { id: "event_management", label: "EVENT MANAGEMENT", items: [
      { to: "/imagine", icon: Sparkles, label: "Event Planning & Design", testId: NAV.eventsLink },
    ]},
    { id: "sourcing_procurement", label: "SOURCING & PROCUREMENT", items: [
      { to: "/finance/purchase-orders", icon: Package,       label: "Purchase Orders",       testId: "nav-purchase-orders" },
      { to: "/ap/acceptance",           icon: ClipboardList, label: "Service Acceptance",    testId: "nav-service-acceptance" },
      { to: "/ap/invoice-review",       icon: FileText,      label: "Invoice Review",        testId: "nav-invoice-review" },
      { to: "/ap/credit-notes",         icon: FileText,      label: "Supplier Credit Notes", testId: "nav-credit-notes" },
    ]},
    { id: "finance_accounts", label: "FINANCE & ACCOUNTS", items: [
      { to: "/ap/payments",      icon: DollarSign,    label: "Supplier Payments",  testId: "nav-payments" },
      { to: "/ar/invoices",      icon: Send,          label: "AR Invoices",        testId: "nav-ar-invoices" },
      { to: "/ar/receipts",      icon: DollarSign,    label: "Customer Receipts",  testId: "nav-ar-receipts" },
      { to: "/ar/collections",   icon: Banknote,      label: "Collections",        testId: "nav-ar-collections" },
      { to: "/ar/credit-notes",  icon: FileText,      label: "AR Credit Notes",    testId: "nav-ar-credit-notes" },
      { to: "/ar/payment-plans", icon: CalendarRange, label: "Payment Plans",      testId: "nav-ar-payment-plans" },
      { to: "/ar/aging",         icon: BarChart2,     label: "AR Aging",           testId: "nav-ar-aging" },
      { to: "/gl/journals",      icon: BookOpen,      label: "GL Journals",        testId: "nav-gl-journals" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── System Administrator: Administration + Sourcing + Finance ────────────
  TENANT_ADMIN: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "administration", label: "ADMINISTRATION", items: [
      { to: "/business-setup",              icon: Settings2,   label: "Business Setup",         testId: "nav-business-setup" },
      { to: "/administration/integrations", icon: Zap,         label: "Integrations",           testId: "nav-integrations" },
      { to: "/administration",              icon: ShieldCheck, label: "Administration",         testId: NAV.adminLink },
    ]},
    { id: "venue_commerce", label: "VENUE COMMERCE", items: [
      { to: "/venue-commerce/catalogue",            icon: Hotel,     label: "Venue Catalogue",      testId: "nav-venue-catalogue" },
      { to: "/venue-commerce/partner-applications", icon: FileText,  label: "Partner Applications", testId: "nav-venue-partner-apps" },
      { to: "/destination-preview/hyderabad",       icon: Compass,   label: "Destination Preview",  testId: "nav-destination-preview" },
    ]},
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",         testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ]},
    { id: "sourcing_procurement", label: "SOURCING & PROCUREMENT", items: [
      { to: "/finance/purchase-orders", icon: Package,       label: "Purchase Orders",       testId: "nav-purchase-orders" },
      { to: "/ap/acceptance",           icon: ClipboardList, label: "Service Acceptance",    testId: "nav-service-acceptance" },
      { to: "/ap/invoice-review",       icon: FileText,      label: "Invoice Review",        testId: "nav-invoice-review" },
      { to: "/ap/payments",             icon: DollarSign,    label: "Supplier Payments",     testId: "nav-payments" },
      { to: "/ap/credit-notes",         icon: FileText,      label: "Supplier Credit Notes", testId: "nav-credit-notes" },
    ]},
    { id: "finance_accounts", label: "FINANCE & ACCOUNTS", items: [
      { to: "/finance/dashboard",      icon: BarChart2,     label: "Financial Dashboard", testId: "nav-financial-dashboard" },
      { to: "/ar/invoices",            icon: Send,          label: "AR Invoices",         testId: "nav-ar-invoices" },
      { to: "/ar/receipts",            icon: DollarSign,    label: "Customer Receipts",   testId: "nav-ar-receipts" },
      { to: "/ar/collections",         icon: Banknote,      label: "Collections",         testId: "nav-ar-collections" },
      { to: "/ar/credit-notes",        icon: FileText,      label: "AR Credit Notes",     testId: "nav-ar-credit-notes" },
      { to: "/ar/refunds",             icon: DollarSign,    label: "Refund Requests",     testId: "nav-ar-refunds" },
      { to: "/ar/payment-plans",       icon: CalendarRange, label: "Payment Plans",       testId: "nav-ar-payment-plans" },
      { to: "/ar/gateway-payments",    icon: QrCode,        label: "Gateway Payments",    testId: "nav-gateway-payments" },
      { to: "/ar/aging",               icon: BarChart2,     label: "AR Aging",            testId: "nav-ar-aging" },
      { to: "/ar/master-data",         icon: Settings2,     label: "AR Configuration",    testId: "nav-ar-master-data" },
      { to: "/gl/journals",            icon: BookOpen,      label: "GL Journals",         testId: "nav-gl-journals" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Event Planner: Event management workbench ─────────────────────────────
  EVENT_PLANNER_DESIGNER: [
    { id: "home", label: null, items: [
      { to: "/planner/cases", icon: LayoutDashboard, label: "My Planning Cases", testId: "nav-planner-cases" },
    ]},
    { id: "event_management", label: "EVENT MANAGEMENT", items: [
      { to: "/planner/cases",     icon: ClipboardList,     label: "Assigned Cases",   testId: "nav-assigned-cases" },
      { to: "/engage/leads",      icon: Target,            label: "Leads (Assigned)", testId: NAV.leadsLink },
      { to: "/crm/activities",    icon: Activity,          label: "Activities",       testId: "nav-activities" },
      { to: "/engage/follow-ups", icon: MessageSquarePlus, label: "Follow-ups",       testId: "nav-followups" },
      { to: "/crm/inbox",         icon: MessageCircle,     label: "Communications",   testId: "nav-comm-inbox" },
    ]},
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",         testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Client Relationship Manager (SALES_CRM_USER) ──────────────────────────
  SALES_CRM_USER: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "crm_sales", label: "CRM & SALES", items: _CRM_ITEMS_BASE() },
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video,        label: "Meetings",         testId: "nav-connect-meetings" },
      { to: "/connect/schedule", icon: CalendarPlus, label: "Schedule Meeting", testId: "nav-connect-schedule" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Finance Manager: Event context + Sourcing + Finance + Approval admin ──
  FINANCE_APPROVER: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "event_management", label: "EVENT MANAGEMENT", items: [
      { to: "/imagine", icon: Sparkles, label: "Event Planning", testId: NAV.eventsLink },
    ]},
    { id: "sourcing_procurement", label: "SOURCING & PROCUREMENT", items: [
      { to: "/finance/purchase-orders", icon: Package,       label: "Purchase Orders",       testId: "nav-purchase-orders" },
      { to: "/ap/acceptance",           icon: ClipboardList, label: "Service Acceptance",    testId: "nav-service-acceptance" },
      { to: "/ap/invoice-review",       icon: FileText,      label: "Invoice Review",        testId: "nav-invoice-review" },
      { to: "/ap/payments",             icon: DollarSign,    label: "Supplier Payments",     testId: "nav-payments" },
      { to: "/ap/credit-notes",         icon: FileText,      label: "Supplier Credit Notes", testId: "nav-credit-notes" },
    ]},
    { id: "finance_accounts", label: "FINANCE & ACCOUNTS", items: [
      { to: "/finance/dashboard",      icon: BarChart2,     label: "Financial Dashboard", testId: "nav-financial-dashboard" },
      { to: "/ar/invoices",            icon: Send,          label: "AR Invoices",         testId: "nav-ar-invoices" },
      { to: "/ar/receipts",            icon: DollarSign,    label: "Customer Receipts",   testId: "nav-ar-receipts" },
      { to: "/ar/collections",         icon: Banknote,      label: "Collections",         testId: "nav-ar-collections" },
      { to: "/ar/credit-notes",        icon: FileText,      label: "AR Credit Notes",     testId: "nav-ar-credit-notes" },
      { to: "/ar/refunds",             icon: DollarSign,    label: "Refund Requests",     testId: "nav-ar-refunds" },
      { to: "/ar/payment-plans",       icon: CalendarRange, label: "Payment Plans",       testId: "nav-ar-payment-plans" },
      { to: "/ar/gateway-payments",    icon: QrCode,        label: "Gateway Payments",    testId: "nav-gateway-payments" },
      { to: "/ar/aging",               icon: BarChart2,     label: "AR Aging",            testId: "nav-ar-aging" },
      { to: "/ar/master-data",         icon: Settings2,     label: "AR Configuration",    testId: "nav-ar-master-data" },
      { to: "/gl/journals",            icon: BookOpen,      label: "GL Journals",         testId: "nav-gl-journals" },
    ]},
    { id: "administration", label: "ADMINISTRATION", items: [
      { to: "/settings/approval-policies", icon: ShieldCheck, label: "Approval Policies", testId: "nav-approval-policies" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],

  // ── Operations Coordinator: Event day workspace ───────────────────────────
  OPERATIONS_COORDINATOR: [
    { id: "home", label: null, items: [
      { to: "/home", icon: LayoutDashboard, label: "Home", testId: "nav-home" },
    ]},
    { id: "event_management", label: "EVENT MANAGEMENT", items: [
      { to: "/events",         icon: Calendar, label: "My Events",  testId: "nav-ops-events" },
      { to: "/crm/activities", icon: Activity, label: "Activities", testId: "nav-activities" },
    ]},
    { id: "argov_connect", label: "ARGOV CONNECT", items: [
      { to: "/connect/meetings", icon: Video, label: "Meetings", testId: "nav-connect-meetings" },
    ]},
    { id: "uat", label: "UAT", feedbackAction: true, items: [
      { to: "/uat", icon: ClipboardList, label: "UAT Control Center", testId: NAV.uatLink },
    ]},
    { id: "help", label: "HELP", items: [
      { to: "/guide", icon: HelpCircle, label: "User Guide", testId: "nav-user-guide" },
    ]},
  ],
};

/** Return the nav sections to show for a given role (defaults to full catalogue). */
function getRoleNavSections(role) {
  return ROLE_NAV_SECTIONS_MAP[role] || NAV_SECTIONS;
}

/**
 * NAV-R2: Apply tenant-specific label overrides from LabelContext to nav items.
 * Only overrides exact label matches to preserve role-qualified labels
 * (e.g. "Leads (Assigned)" for EVENT_PLANNER_DESIGNER remains unchanged).
 */
function applyNavLabels(sections, leadsLabel, opportunitiesLabel) {
  if (leadsLabel === "Leads" && opportunitiesLabel === "Opportunities") return sections;
  return sections.map(section => ({
    ...section,
    items: section.items.map(item => {
      if (item.to === "/engage/leads" && item.label === "Leads") {
        return { ...item, label: leadsLabel };
      }
      if (item.to === "/crm/opportunities" && item.label === "Opportunities") {
        return { ...item, label: opportunitiesLabel };
      }
      return item;
    }),
  }));
}

const _isUATPersona = (user) =>
  user?.email?.toLowerCase().endsWith("@prashasta.uat") || user?.is_uat_persona;

export default function Layout() {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [coPilotOpen, setCoPilotOpen] = useState(false);
  const inboxBadge = useInboxBadge();

  // NAV-R2: tenant-specific label overrides from Admin → Terminology
  const leadsLabel        = useLabel("nav.leads", "Leads");
  const opportunitiesLabel = useLabel("nav.opportunities", "Opportunities");

  const ctx = resolveContext(location.pathname);
  const isUATPersona = _isUATPersona(user);
  const roleLabel = ROLE_LABELS[user?.role] || user?.role || "";
  const activeNavSections = applyNavLabels(getRoleNavSections(user?.role), leadsLabel, opportunitiesLabel);

  // UAT section: permanently filtered — UAT tooling is accessible only via the /uat direct URL
  const visibleSections = activeNavSections.filter(s => s.id !== "uat");

  const handleLogout = async () => {
    await logout();
    navigate("/uat-login");
  };

  const handleSwitchRole = async () => {
    await logout();
    navigate("/uat-login");
  };

  const onNavClick = () => setMobileOpen(false);

  // Prashasta premium nav link classes
  const navLinkClass = (isActive) =>
    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${
      isActive
        ? "font-semibold border-l-[3px] pl-[9px]"
        : "hover:bg-[#EFE9DE] hover:text-[#1C1917]"
    } ${collapsed ? "md:justify-center md:px-2" : ""}`;

  const navLinkStyle = (isActive) => isActive
    ? { background: "#F4E8EB", color: "#7A1A2B", borderLeftColor: "var(--brand-burgundy)" }
    : { color: "#57534E" };

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "#F4EFEA" }}>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────── */}
      <aside
        data-testid={NAV.sidebar}
        className={[
          "flex flex-col border-r",
          "fixed inset-y-0 left-0 z-40 w-72",
          "transition-transform duration-200 ease-in-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "md:relative md:inset-y-auto md:left-auto md:translate-x-0 md:flex-shrink-0 md:z-auto",
          collapsed ? "md:w-[52px]" : "md:w-[220px]",
        ].join(" ")}
        style={{ background: "#FAF7F2", borderColor: "#E7E0D8" }}
      >
        {/* Brand header */}
        <div
          className={`flex items-center border-b ${collapsed ? "md:justify-center md:px-2 px-3 py-3" : "px-3 py-3 gap-2"}`}
          style={{ borderColor: "#E7E0D8" }}
        >
          {/* Collapsed desktop: show square icon mark only */}
          <img
            src="/prashasta-logo-cropped.png"
            alt="Prashasta"
            className={collapsed ? "hidden md:block flex-shrink-0 object-contain" : "hidden"}
            style={{ width: '34px', height: '34px', objectFit: 'contain' }}
            data-testid="sidebar-brand-logo-mark"
          />
          {/* Expanded desktop + mobile: show full horizontal logo */}
          <img
            src="/prashasta-logo.png"
            alt="Prashasta Events"
            className={`object-contain flex-shrink-0 ${collapsed ? "md:hidden" : ""}`}
            style={{ height: '38px', width: 'auto', maxWidth: '160px' }}
            data-testid="sidebar-brand-logo"
          />
          {/* Mobile close button */}
          <button
            className="md:hidden ml-auto p-1 rounded hover:bg-[#F0EAE0] transition-colors"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            style={{ color: "#78716C" }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tenant badge — include UAT TEST MODE indicator */}
        {isUATPersona ? (
          <div className={`mx-2 mt-2 space-y-1.5 ${collapsed ? "md:hidden" : ""}`}>
            <div
              className="px-2.5 py-1.5 rounded border border-amber-300 bg-amber-50"
              data-testid="uat-sidebar-mode-badge"
            >
              <div className="flex items-center gap-1">
                <ShieldAlert className="w-2.5 h-2.5 text-amber-600 flex-shrink-0" />
                <p className="text-[9px] font-bold uppercase tracking-widest text-amber-700">UAT Test Mode</p>
              </div>
              <p className="text-xs font-semibold truncate mt-0.5 text-amber-900" data-testid="uat-testing-as-label">
                Testing as: {roleLabel}
              </p>
            </div>
          </div>
        ) : (
          <div
            className={`mx-2 mt-2 px-2.5 py-1.5 rounded-xl border ${collapsed ? "md:hidden" : ""}`}
            style={{ background: "#F0E8DA", border: "1px solid #E7D8C0" }}
            data-testid={NAV.tenantBadge}
          >
            <p className="text-[9px] font-semibold uppercase tracking-widest" style={{ color: "#78716C" }}>Tenant</p>
            <p className="text-xs font-semibold truncate mt-0.5" style={{ color: "var(--brand-gold)" }}>
              {user?.tenant_name || user?.tenant_id}
            </p>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 overflow-y-auto">
          {visibleSections.map((section, idx) => (
            <div key={section.id}>
              {idx > 0 && (
                <>
                  {collapsed && (
                    <div className="hidden md:block my-2 border-t border-[#E5E5E5] mx-1" />
                  )}
                  <div className={`pt-3 pb-1 ${collapsed ? "md:hidden" : ""}`}>
                    <p
                      className="text-[9px] font-semibold uppercase tracking-widest px-3"
                      style={{ color: "#78716C" }}
                    >
                      {section.label}
                    </p>
                  </div>
                </>
              )}

              {/* Co-Pilot launcher — injected into the HELP section, just above User Guide */}
              {section.id === "help" && (
                <button
                  data-testid="copilot-sidebar-btn"
                  onClick={() => { setCoPilotOpen(o => !o); onNavClick(); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${
                    coPilotOpen
                      ? "font-semibold border-l-[3px] pl-[9px]"
                      : "hover:bg-[#EFE9DE] hover:text-[#1C1917]"
                  } ${collapsed ? "md:justify-center md:px-2" : ""}`}
                  style={coPilotOpen
                    ? { background: "#F4E8EB", color: "#7A1A2B", borderLeftColor: "var(--brand-burgundy)" }
                    : { color: "#57534E" }
                  }
                  title={collapsed ? "Co-Pilot" : undefined}
                >
                  <Compass className="w-4 h-4 flex-shrink-0" />
                  <span className={`flex-1 text-left ${collapsed ? "md:hidden" : ""}`}>Prashasta Assistant</span>
                </button>
              )}

              {section.items.map(({ to, icon: Icon, label, testId }) => (
                <NavLink
                  key={`${to}--${label}`}
                  to={to}
                  data-testid={testId}
                  onClick={onNavClick}
                  className={({ isActive }) => navLinkClass(isActive)}
                  style={({ isActive }) => navLinkStyle(isActive)}
                  title={collapsed ? label : undefined}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className={`flex-1 ${collapsed ? "md:hidden" : ""}`}>{label}</span>
                  {testId === "nav-comm-inbox" && inboxBadge > 0 && !collapsed && (
                    <span
                      data-testid="inbox-badge-count"
                      className="ml-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 flex-shrink-0"
                    >
                      {inboxBadge > 99 ? "99+" : inboxBadge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User info + logout */}
        <div className="border-t p-2 space-y-1" style={{ borderColor: "#E7E0D8" }}>
          <div
            className={`px-3 py-2 rounded-xl ${collapsed ? "md:hidden" : ""}`}
            style={{ background: "#F0E8DA" }}
            data-testid={NAV.userInfo}
          >
            <p className="text-xs font-semibold truncate" style={{ color: "#1C1917" }}>{user?.name}</p>
            <p className="text-[10px] truncate" style={{ color: "#78716C" }}>{user?.email}</p>
            <span
              className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
              style={{ background: "var(--brand-gold-light)", color: "var(--brand-gold)" }}
            >
              {user?.role}
            </span>
          </div>

          {/* Switch Role — only for UAT personas */}
          {isUATPersona && (
            <button
              data-testid="uat-switch-role-btn"
              onClick={handleSwitchRole}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded text-sm text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors font-medium ${collapsed ? "md:justify-center md:px-2" : ""}`}
              title={collapsed ? "Switch Role" : undefined}
            >
              <RefreshCw className="w-4 h-4 flex-shrink-0" />
              <span className={collapsed ? "md:hidden" : ""}>Switch Role</span>
            </button>
          )}
          <button
            onClick={handleLogout}
            data-testid={NAV.logoutBtn}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${collapsed ? "md:justify-center md:px-2" : ""}`}
            style={{ color: "#78716C" }}
            onMouseEnter={e => { e.currentTarget.style.color = "#B91C1C"; e.currentTarget.style.background = "rgba(185,28,28,0.06)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#78716C"; e.currentTarget.style.background = "transparent"; }}
            title={collapsed ? "Logout" : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span className={collapsed ? "md:hidden" : ""}>Logout</span>
          </button>
        </div>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex absolute bottom-4 w-6 h-6 rounded-full border items-center justify-center transition-colors hover:border-[var(--brand-gold)] z-10"
          style={{
            left: collapsed ? "40px" : "208px",
            background: "#FFFFFF",
            color: "#78716C",
            borderColor: "#E7E0D8",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "var(--brand-gold)"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#78716C"; }}
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>

        {/* ARGOV attribution */}
        {!collapsed && (
          <div
            className="px-3 pb-2 pt-1"
            style={{ borderTop: '1px solid #E7E0D8' }}
            data-testid="argov-attribution"
          >
            <p className="text-[9px] text-center" style={{ color: '#B8AFA6', letterSpacing: '0.05em' }}>
              Powered by ARGOV Technologies
            </p>
          </div>
        )}
      </aside>

      {/* ── Main content ──────────────────────────────────────────────── */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top bar */}
        <header
          className="h-12 flex items-center justify-between px-4 md:px-6 flex-shrink-0"
          style={{
            background: "#FFFFFF",
            borderBottom: "1px solid #E7E0D8",
          }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-1.5 rounded transition-colors flex-shrink-0"
              style={{ color: "#78716C" }}
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              onMouseEnter={e => { e.currentTarget.style.background = "#F0EAE0"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              <Menu className="w-5 h-5" />
            </button>
            <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: "var(--brand-gold)" }} />
            <span
              className="text-sm font-semibold truncate font-display"
              style={{ color: "#1C1917" }}
            >
              {user?.tenant_name || user?.tenant_id}
            </span>
            {user?.tenant_number && (
              <span className="text-xs hidden sm:inline" style={{ color: "#78716C" }}>
                {user.tenant_number}
              </span>
            )}
          </div>

          {/* Breadcrumb + UAT TEST MODE header indicator */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <CRMGlobalSearch />
            {isUATPersona ? (
              <div
                data-testid="uat-header-badge"
                className="hidden sm:flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50"
              >
                <ShieldAlert className="w-3 h-3 text-amber-600" />
                <span className="text-[9px] font-bold uppercase tracking-widest text-amber-700">
                  UAT
                </span>
                <span className="text-[9px] text-amber-600 border-l border-amber-300 pl-1.5">
                  {roleLabel}
                </span>
              </div>
            ) : process.env.REACT_APP_ENVIRONMENT === "UAT" && (
              <span
                className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-widest"
                style={{ background: "#FFF4E5", color: "#8C4B00", border: "1px solid #FE9339" }}
              >
                UAT
              </span>
            )}
            <span className="text-xs hidden sm:inline" style={{ fontFamily: "Consolas, monospace", color: "#78716C" }}>
              <span style={{ color: "#D6CEBF" }}>{ctx.section}</span>
              {ctx.page && (
                <>
                  <span style={{ color: "#D6CEBF", margin: "0 6px" }}>/</span>
                  <span style={{ color: "#57534E" }}>{ctx.page}</span>
                </>
              )}
            </span>
            <span className="text-xs sm:hidden" style={{ color: "#57534E" }}>
              {ctx.page || ctx.section}
            </span>
            {/* DCC-03: Notification Bell */}
            <NotificationBell />
            {/* Co-Pilot launcher */}
            <button
              data-testid="copilot-header-btn"
              onClick={() => setCoPilotOpen(o => !o)}
              className="relative flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
              style={{
                color:      coPilotOpen ? "var(--brand-gold, #C9A15F)" : "#78716C",
                background: coPilotOpen ? "#F0E8DA" : "transparent",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#F0EAE0"; }}
              onMouseLeave={e => { e.currentTarget.style.background = coPilotOpen ? "#F0E8DA" : "transparent"; }}
              aria-label={coPilotOpen ? "Close Prashasta Assistant" : "Open Prashasta Assistant"}
              title="Prashasta Assistant"
            >
              <Compass className="w-4 h-4" />
            </button>
            {/* Live indicator */}
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: "#2E844A", boxShadow: "0 0 5px rgba(46,132,74,0.4)" }}
            />
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto" style={{ background: "#F4EFEA" }}>
            <Outlet />
          </div>
          {/* Prashasta Assistant Drawer — inline on desktop, overlay on mobile */}
          <CoPilotDrawer
            open={coPilotOpen}
            onClose={() => setCoPilotOpen(false)}
            user={user}
            navSections={visibleSections}
          />
        </div>
      </main>
    </div>
  );
}
